import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import useChat from '../hooks/useChat';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import { ensureINSToken, handleFailedRefresh } from '../../services/interactiveServiceFetch';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import TodosSection from './TodosSection';
import ChatEmptyState from './ChatEmptyState';
import PreviewPanel from './PreviewPanel';
import SessionList from './SessionList';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { MESSAGE_ROLES } from '../constants';
import { getSelectedLanguage } from '../../selectors/locale';
import css from './ChatView.css';
import { Button } from '../../components/widgets';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const ChatView = ({ readOnly = false, initialPayload, runPath, sessionType, isV2, headerActions }) => {
    const messagesEndRef = useRef(null);
    const hasStartedRef = useRef(false);
    const [showSessionSidebar, setShowSessionSidebar] = useState(true);
    const [dashboardArtifacts, setDashboardArtifacts] = useState(null);

    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(false);

    useEffect(() => {
        ensureINSToken()
            .then(result => {
                if (result.ok) return;
                handleFailedRefresh(result);
                setAuthError(true);
            })
            .finally(() => setAuthLoading(false));
    }, []);

    const {
        messages,
        input,
        setInput,
        handleInputChange,
        handleSubmit,
        isLoading,
        error,
        stopError,
        reload,
        stop,
        currentSessionId,
        sessions,
        isLoadingSessions,
        loadingSessionId,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChat({
        isV2,
        runPath,
        sessionType,
        loadSessionsOnMount: !readOnly && !authLoading,
    });

    useEffect(() => {
        if (!initialPayload || hasStartedRef.current || authLoading || authError) return;
        hasStartedRef.current = true;
        handleSubmit(null, {
            content: initialPayload.content,
            body: initialPayload.body,
        });
    }, [initialPayload, handleSubmit, authLoading, authError]);

    const prevSessionIdRef = useRef(currentSessionId);
    useEffect(() => {
        const sessionChanged = prevSessionIdRef.current !== currentSessionId;
        prevSessionIdRef.current = currentSessionId;
        messagesEndRef.current?.scrollIntoView({ behavior: sessionChanged ? 'auto' : 'smooth' });
    }, [messages, currentSessionId]);

    const activeLanguage = useSelector(getSelectedLanguage);

    const {
        isListening: isMicListening,
        isSupported: isMicSupported,
        toggle: toggleMic,
        stop: stopMic,
        syncBaseText: syncMicBaseText,
    } = useSpeechRecognition({ onTranscript: setInput, lang: activeLanguage });

    const onMicClick = useCallback(() => {
        toggleMic(input);
    }, [toggleMic, input]);

    useEffect(() => {
        if (isLoading && isMicListening) stopMic();
    }, [isLoading, isMicListening, stopMic]);

    useEffect(() => {
        if (isLoading) return;
        syncMicBaseText(input);
    }, [input, syncMicBaseText, isLoading]);

    const hasMessages = messages.length > 0;

    const activeSteps = useMemo(() => {
        const activeAssistantMessage = [...messages]
            .reverse()
            .find(msg => msg.role === MESSAGE_ROLES.ASSISTANT && msg.steps && msg.steps.length > 0);
        return activeAssistantMessage?.steps || [];
    }, [messages]);

    const lastMessageDashboardArtifacts = useMemo(() => {
        const lastMessage = messages?.[messages.length - 1];
        if (!lastMessage || lastMessage.role !== MESSAGE_ROLES.ASSISTANT) return null;
        return lastMessage.content?.filter(block => block.is_final_analysis);
    }, [messages]);

    const onDashboardClick = artifacts => setDashboardArtifacts(artifacts);

    const previewArtifacts = dashboardArtifacts?.length > 0 ? dashboardArtifacts : lastMessageDashboardArtifacts;

    if (authLoading && !initialPayload) {
        return (
            <div className={css.chatView}>
                <div className={css.loading}>
                    <Icon name="loader" size={IconSizes.LARGE} className={css.spinningLoader} />
                    <div className={css.loadingText}><Translate id={`${TRANSLATION_PREFIX}.AUTH_LOADING`} /></div>
                </div>
            </div>
        );
    }

    if (authError) {
        return (
            <div className={css.chatView}>
                <div className={css.authError}>
                    <Icon name="alert-triangle" size={IconSizes.LARGE} className={css.authErrorIcon} />
                    <div className={css.authErrorText}><Translate id={`${TRANSLATION_PREFIX}.AUTH_ERROR`} /></div>
                </div>
            </div>
        );
    }

    const onNewChat = () => {
        clearCurrentSession();
        setDashboardArtifacts(null);
    };

    const toggleSessionSidebar = () => setShowSessionSidebar(!showSessionSidebar);

    const onSessionDelete = async sessionId => await deleteSession(sessionId);

    const onSessionSelect = async sessionId => {
        if (loadingSessionId) return;
        if (sessionId !== currentSessionId) {
            setDashboardArtifacts(null);
            await loadSession(sessionId);
        }
    };

    const onSessionUpdate = async (sessionId, updates) => {
        if (updates.title) await updateSessionTitle(sessionId, updates.title);
    };

    return (
        <div className={css.chatView}>
            {!readOnly && (
                <div className={css.header}>
                    <div className={css.title}><Translate id={`${TRANSLATION_PREFIX}.TITLE`} /></div>
                    <div className={css.headerActions}>
                        {headerActions}
                        <Button Icon="plus" onClick={onNewChat} disabled={isLoading}>
                            <Translate id={`${TRANSLATION_PREFIX}.NEW_CHAT`} />
                        </Button>
                    </div>
                </div>
            )}
            <div className={css.body}>
                {!readOnly && (
                    <>
                        <div className={classNames(css.sessionSidebar, { [css.collapsed]: !showSessionSidebar })}>
                            <SessionList
                                sessions={sessions}
                                currentSessionId={currentSessionId}
                                loadingSessionId={loadingSessionId}
                                onSessionSelect={onSessionSelect}
                                onSessionDelete={onSessionDelete}
                                onSessionUpdate={onSessionUpdate}
                                isLoading={isLoadingSessions}
                            />
                        </div>
                        <Icon
                            name={showSessionSidebar ? 'chevron-left' : 'chevron-right'}
                            size={IconSizes.MEDIUM}
                            className={classNames(css.sidebarButton, { [css.collapsed]: !showSessionSidebar })}
                            onClick={toggleSessionSidebar}
                            hoverText={showSessionSidebar ? 'Collapse sidebar' : 'Expand sidebar'}
                        />
                    </>
                )}
                <div className={css.content}>
                    <div className={css.messagesContainer}>
                        {!readOnly && !hasMessages && (
                            <ChatEmptyState
                                onSuggestionClick={query => handleSubmit(null, { content: query })}
                                input={input}
                                handleInputChange={handleInputChange}
                                handleSubmit={handleSubmit}
                                isLoading={isLoading}
                                stop={stop}
                                onMicClick={onMicClick}
                                isMicListening={isMicListening}
                                micSupported={isMicSupported}
                            />
                        )}
                        {hasMessages && (
                            <div className={css.messagesList}>
                                {messages.map(message => (
                                    <ChatMessage
                                        key={message.id}
                                        message={message}
                                        onDashboardClick={onDashboardClick}
                                    />
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                        {(error || stopError) && (
                            <div className={css.chatError}>
                                <Icon
                                    name="alert-triangle"
                                    variant={IconVariants.ERROR}
                                    size={IconSizes.SMALL}
                                    className={css.chatErrorIcon}
                                    interactive={false}
                                />
                                <span className={css.chatErrorMessage}>{error || stopError}</span>
                                {error && (
                                    <button onClick={reload} className={css.chatErrorRetry}>
                                        <Translate id={`${TRANSLATION_PREFIX}.RETRY`} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {!!activeSteps.length && <TodosSection steps={activeSteps} />}
                    {!readOnly && hasMessages && (
                        <ChatInput
                            input={input}
                            handleInputChange={handleInputChange}
                            handleSubmit={handleSubmit}
                            isLoading={isLoading}
                            stop={stop}
                            onMicClick={onMicClick}
                            isMicListening={isMicListening}
                            micSupported={isMicSupported}
                        />
                    )}
                </div>
                {previewArtifacts?.length > 0 && <PreviewPanel key={currentSessionId} artifacts={previewArtifacts} />}
            </div>
        </div>
    );
};

ChatView.propTypes = {
    readOnly: PropTypes.bool,
    initialPayload: PropTypes.shape({
        content: PropTypes.string.isRequired,
        body: PropTypes.object.isRequired,
    }),
    runPath: PropTypes.string.isRequired,
    sessionType: PropTypes.string.isRequired,
    isV2: PropTypes.bool,
    headerActions: PropTypes.node,
};

export default ChatView;
